<template>
  <Widget
    widgetName="longest-unresolved-findings"
    preventDataFetch
    hideTimeMenu
    :customLoading="loading"
    :customWidgetIsEmpty="widgetIsEmpty"
    :customWidgetError="widgetError"
    :cta="{
      action: goToExplore,
      text: $t('common.button.view_more'),
    }"
    @locationOptionChange="handleLocationOptionChange"
  >
    <template v-slot:content>
      <div class="mx-6 top-findings-table-container">
        <v-data-table
          class="rounded findings-table"
          mobile-breakpoint="0"
          disable-sort
          disable-filtering
          disable-pagination
          hide-default-footer
          :headers="headers"
          :items="findings"
          :findings-per-page="5"
        >
          <template v-slot:item.name="{ item }">
            <p
              v-snip="2"
              class="ma-0 pa-0 font-size-10 cursor-pointer link-td blue-grey--text text--darken-3 table-finding-name-link"
              :title="item.name"
              @click="openEntityDrawer(item)"
            >
              {{ item.name }}
            </p>
          </template>

          <template v-slot:item.assignees="{ item }">
            <Assignees
              v-if="item.assignees.length !== 0"
              disabled
              fontSize="8px"
              avatarSize="16px"
              :accounts="item.assignees"
              :maxFindingsToShow="5"
            />
            <p
              v-else
              class="ma-0 pa-0 blue-grey--text text--darken-3 font-size-10"
            >
              -
            </p>
          </template>
          <template v-slot:item.daysOpen="{ item }">
            <p class="ma-0 pa-0 blue-grey--text text--darken-3 font-size-10">
              {{ item.daysOpen }}
            </p>
          </template>
        </v-data-table>
      </div>
    </template>
  </Widget>
</template>

<script>
import Widget from '../SharedComponents/WidgetBase.vue'
import Assignees from '@/components/AssigneesSelector/AssigneesSelection.vue'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import api from '@/api'
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

export default {
  name: 'LongestUnresolvedFindings',

  mixins: [mixin, trackerMixin],

  components: {
    Widget,
    Assignees,
  },

  data() {
    return {
      findings: [],
      loading: false,
      widgetIsEmpty: false,
      widgetError: false,
      selectedSite: null,
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    headers() {
      return [
        {
          text: this.$t('common.finding_one'),
          align: 'start',
          sortable: false,
          value: 'name',
          class:
            'blue-grey--text text--darken-1 font-size-10 font-weight-600 px-2 py-0 ma-0 top-findings-header',
          cellClass: 'font-weight-500 ps-2 ma-0 pa-0 py-1',
        },
        {
          text: this.$t('common.assignee_one'),
          align: 'start',
          sortable: false,
          value: 'assignees',
          class:
            'blue-grey--text text--darken-1 font-size-10 font-weight-600 px-2 py-0 ma-0 top-findings-header',
          cellClass:
            'blue-grey--text text--darken-3 font-size-10 font-weight-500 ps-2 ma-0 pa-0',
          width: ' 100px',
        },
        {
          text: this.$t('insights.days_open'),
          align: 'start',
          sortable: false,
          value: 'daysOpen',
          class:
            'blue-grey--text text--darken-1 font-size-10 font-weight-600 px-2 py-0 ma-0 top-findings-header',
          cellClass:
            'blue-grey--text text--darken-3 font-size-10 font-weight-500 ps-2 ma-0 pa-0',
          width: '100px',
        },
      ]
    },

    requestParams() {
      const params = {
        organizationId: this.organizationId,
        limit: 6,
        skip: 0,
        sort: { by: 'createdAt', order: 'asc' },
        filters: {
          status: ['inProgress', 'onHold', 'open'],
        },
        relations: ['assignees.photo'],
      }

      if (this.selectedSite !== 'global') {
        params.filters.sites = [this.selectedSite]
      }

      return params
    },
  },

  methods: {
    openEntityDrawer(item) {
      eventHub.$emit(HUB_EVENTS.ENTITY_DRAWER.UPDATE, {
        ...item,
        entityType: 'finding',
      })
    },

    async getInsight() {
      try {
        this.loading = true

        const params = this.requestParams
        const response = await api.findings.getAll(params)

        this.getFindingsList(response)

        this.widgetError = false
      } catch (err) {
        this.widgetError = true
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    getFindingsList(response) {
      const currentDate = new Date()
      const findings =
        response?.data?.results?.map(finding => {
          return {
            ...finding,
            name: finding.situation || '-',
            daysOpen: utils.dates.calculateDifferenceInDays(
              finding.createdAt,
              currentDate
            ),
          }
        }) || []

      this.findings = this.ensureSixFindings(findings)
      this.widgetIsEmpty = response.data.totalRecordsInData === 0
    },

    ensureSixFindings(findings) {
      const requiredCount = this.requestParams?.limit ?? 6
      const missingCount = requiredCount - findings.length

      if (missingCount > 0) {
        const defaultFindings = Array.from({ length: missingCount }, () => ({
          name: '-',
          daysOpen: '-',
          assignees: [],
        }))

        return [...findings, ...defaultFindings]
      }

      return findings
    },

    goToExplore() {
      const query = {
        sort: 'createdAt-asc',
        types: ['finding'],
        status: ['inProgress', 'onHold', 'open'],
      }

      if (this.selectedSite !== 'global') {
        query.sites = [this.selectedSite]
      }

      return this.openInNewTab({
        name: 'explore',
        query,
      })
    },

    handleLocationOptionChange(option) {
      this.selectedSite = option
      this.getInsight()
    },
  },
}
</script>
<style lang="scss">
.table-finding-name-link {
  line-height: 14px;
}

.top-findings-table-container {
  .findings-table {
    border: 1px solid $color-blue-grey-lighten2;
  }

  .top-findings-header {
    height: 23px !important;
  }

  .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 30px !important;
  }
}
</style>
